import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import type { LoginData, LoginReq } from '../services/api';
import { authApi } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { clearFCMToken } from '../services/fcm';

interface AuthContextType {
  user: LoginData | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (params: LoginReq) => Promise<LoginData>;
  logout: () => Promise<void>;
  autoLogin: () => Promise<boolean>;
  setUserData: (userData: LoginData, fcmId: string, snsType: number) => void;
  snsSignupData: { snsId: string; snsType: number } | null;
  setSnsSignup: (snsId: string, snsType: number) => void;
  clearSnsSignup: () => void;
  forceLogout: () => void;  // ⭐ 추가
}


const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<LoginData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // ⭐ SNS 회원가입 정보 상태 추가
  const [snsSignupData, setSnsSignupDataState] = useState<{
    snsId: string;
    snsType: number;
  } | null>(null);

  // ⭐ autoLogin 중복 방지
  const isAutoLoginRunning = useRef(false);
  const hasInitialized = useRef(false);
  const navigate = useNavigate();

  // ⭐ 강제 로그아웃 함수 (API 에러 시 호출)
  const forceLogout = useCallback(() => {
    console.log('[Auth] ⚠️ 강제 로그아웃 실행 - 호출 스택:', new Error().stack);

    setUser(null);
    localStorage.removeItem('user');
    clearFCMToken();
    // 로그인 페이지로 이동 (window.location 사용)
    // React Router의 navigate는 컴포넌트 외부에서 사용 불가하므로
    setTimeout(() => {
      navigate('/login', { replace: true });
    }, 0);
  }, [navigate]);

  // ⭐ AUTH_EXPIRED 이벤트 리스너
  useEffect(() => {
    const handleAuthExpired = () => {
      console.log('[Auth] AUTH_EXPIRED 이벤트 수신');
      forceLogout();
    };

    // 이벤트 리스너 등록
    window.addEventListener('auth:expired', handleAuthExpired);

    // 클린업
    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired);
    };
  }, [forceLogout]);

  // Try to restore user session on initial load
  useEffect(() => {

    if (hasInitialized.current) {
      console.log('[Auth] ⚠️ 이미 초기화됨 - 스킵');
      return;
    }


    hasInitialized.current = true;

    const initializeAuth = async () => {
      console.log('[Auth] ===== 초기화 시작 =====');

      // ⭐ 이미 실행 중이면 스킵
      if (isAutoLoginRunning.current) {
        console.log('[Auth] ⚠️ autoLogin 이미 실행 중 - 스킵');
        setIsLoading(false);
        return;
      }

      try {
        const storedUser = localStorage.getItem('user');
        console.log('[Auth] localStorage user:', storedUser);

        if (storedUser) {
          const userData = JSON.parse(storedUser);
          console.log('[Auth] 복원된 userData:', userData);

          setUser(userData);
          console.log('[Auth] ✅ user 상태 복원 완료');

          // ⭐ autoLogin 중복 방지 플래그 설정
          isAutoLoginRunning.current = true;

          // autoLogin 시도
          try {
            const fcmId = localStorage.getItem('fcmId') || '';
            console.log('[Auth] autoLogin 시도...');

            const refreshedData = await authApi.autoLogin({
              memberNo: userData.memberNo,
              apiKey: userData.apiKey,
              fcmId
            });

            console.log('[Auth] ✅ autoLogin 성공');
            setUser(refreshedData);
            localStorage.setItem('user', JSON.stringify(refreshedData));
          } catch (error) {
            console.warn('[Auth] ⚠️ autoLogin 실패:', error);
            console.log('[Auth] 기존 데이터 유지 중...');
          } finally {
            // ⭐ autoLogin 완료
            isAutoLoginRunning.current = false;
          }
        } else {
          console.log('[Auth] ❌ 저장된 사용자 없음');
        }
      } catch (error) {
        console.error('[Auth] ❌ 초기화 에러:', error);
        isAutoLoginRunning.current = false;
      } finally {
        setIsLoading(false);
        console.log('[Auth] ===== 초기화 완료 =====');
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (params: LoginReq): Promise<LoginData> => {
    console.log('[Auth] 로그인 시작');
    setIsLoading(true);

    try {
      const userData = await authApi.login(params);
      console.log('[Auth] 로그인 성공:', userData);

      setUser(userData);

      // User 데이터와 FCM ID 저장
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('fcmId', params.fcmId);

      console.log('[Auth] localStorage 저장 완료');

      return userData;
    } catch (error) {

      console.error('[Auth] 로그인 실패:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Auto login function - can be called on app startup
  const autoLogin = async (): Promise<boolean> => {
    if (!user) {
      console.log('[Auth] autoLogin: user가 없음');
      return false;
    }

    // ⭐ 이미 실행 중이면 스킵
    if (isAutoLoginRunning.current) {
      console.log('[Auth] autoLogin 이미 실행 중 - 스킵');
      return false;
    }

    console.log('[Auth] autoLogin 시작');
    setIsLoading(true);
    isAutoLoginRunning.current = true;

    try {
      const fcmId = localStorage.getItem('fcmId') || '';
      const userData = await authApi.autoLogin({
        memberNo: user.memberNo,
        apiKey: user.apiKey,
        fcmId
      });

      console.log('[Auth] autoLogin 성공');
      // 새로운 API Key로 업데이트
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      return true;

    } catch (error) {
      console.error('[Auth] autoLogin 실패:', error);

      // ⭐ 에러 코드에 따라 다르게 처리
      if (error instanceof Error) {
        console.error('[Auth] autoLogin 에러 상세:', {
          message: error.message,
          stack: error.stack
        });

        if (error.message.includes('인증') ||
          error.message.includes('권한') ||
          error.message.includes('1000') ||
          error.message.includes('401')) {
          console.log('[Auth] ❌ 인증 만료 - 로그아웃 처리');
          forceLogout();
          return false;
        }
      }

      return false;
    } finally {
      setIsLoading(false);
      isAutoLoginRunning.current = false;
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    if (!user) return;

    console.log('[Auth] 로그아웃 시작');
    setIsLoading(true);

    try {
      await authApi.logout({ memberNo: user.memberNo });
      console.log('[Auth] 로그아웃 API 성공');
    } catch (error) {
      console.error('[Auth] 로그아웃 API 실패:', error);
      // 로그아웃 API 실패해도 로컬 데이터는 제거
    } finally {
      setUser(null);
      localStorage.removeItem('user');
      setIsLoading(false);
      clearFCMToken();
      console.log('[Auth] 로그아웃 완료');
    }
  };

  // ⭐ 회원가입 후 직접 데이터 설정 (API 호출 없음)
  const setUserData = (userData: LoginData, fcmId: string, snsType: number) => {
    console.log('[Auth] 사용자 데이터 직접 설정:', userData);

    setUser(userData);

    // localStorage에 저장
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('fcmId', fcmId);
    localStorage.setItem('snsType', String(snsType));

    console.log('[Auth] 데이터 저장 완료');
  };

  // ⭐ SNS 회원가입 정보 설정 함수
  const setSnsSignup = (snsId: string, snsType: number) => {
    console.log('[Auth] SNS 회원가입 정보 저장:', { snsId, snsType });
    setSnsSignupDataState({ snsId, snsType });
  };

  // ⭐ SNS 회원가입 정보 초기화 함수
  const clearSnsSignup = () => {
    console.log('[Auth] SNS 회원가입 정보 초기화');
    setSnsSignupDataState(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        autoLogin,
        setUserData,
        // ⭐ SNS 회원가입 관련 추가
        snsSignupData,
        setSnsSignup,
        clearSnsSignup,
        // ⭐ 강제 로그아웃 추가
        forceLogout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};